export const money = value => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
export const baseFromGp = (cost, gp) => money(Number(cost) * (1 + Number(gp) / 100));
export const gpFromBase = (cost, base) => Number(cost) > 0 ? money((Number(base) - Number(cost)) / Number(cost) * 100) : 0;
export function recommendedBase(ranges, cost) {
  const range = ranges.find(row => money(cost) >= Number(row.MinPrice) && money(cost) <= Number(row.MaxPrice));
  const gp = money(range?.GP_Percentage || 0);
  return { gp, sp: baseFromGp(cost, gp), delivery: money(range?.DeliveryCost || 0), range_id: range?.id ?? null };
}
export function priorQuoteLines(timeline) {
  let events = timeline;
  if (typeof events === 'string') { try { events = JSON.parse(events); } catch { events = []; } }
  return [...(Array.isArray(events) ? events : [])].reverse().find(event => ['quote_sent_to_customer', 'quote_recorded_as_shared_without_delivery'].includes(event?.event) && Array.isArray(event.lines))?.lines || [];
}
export function evaluatePricing(parts, ranges, timeline) {
  const included = parts.filter(part => part.included).sort((a, b) => Number(a.query_part_id) - Number(b.query_part_id));
  const previous = new Map(priorQuoteLines(timeline).map(line => [Number(line.query_part_id), money(line.internal_delivery_allocation || 0)]));
  const total = money(included.reduce((sum, part) => sum + Number(part.cp), 0));
  const required = recommendedBase(ranges, total).delivery;
  const retained = money(included.reduce((sum, part) => sum + (previous.get(Number(part.query_part_id)) || 0), 0));
  const additional = money(Math.max(0, required - retained));
  const target = (included.find(part => !previous.has(Number(part.query_part_id))) || included[0])?.query_part_id;
  const evaluated = parts.map(part => {
    const recommendation = recommendedBase(ranges, part.cp);
    const allocation = part.included ? money((previous.get(Number(part.query_part_id)) || 0) + (Number(part.query_part_id) === Number(target) ? additional : 0)) : 0;
    return { ...part, base_sp: money(part.base_sp), sp: money(Number(part.base_sp) + allocation), recommended_gp_percentage: recommendation.gp,
      recommended_base_selling_price: recommendation.sp, internal_delivery_allocation: allocation,
      below_recommended_gp: !!part.included && money(part.base_sp) < recommendation.sp };
  });
  return { policy_version: 2, total_supplier_cost: total, required_internal_delivery: required, retained_internal_delivery: retained, additional_internal_delivery: additional,
    allocation_target_query_part_id: target ?? null, below_recommended_gp: evaluated.some(part => part.below_recommended_gp), parts: evaluated };
}
export function commercialLine(part) {
  const share = part.included ? money(part.delivery_share || 0) : 0;
  const amount = money(Number(part.sp) + share);
  return { ...part, delivery_share: share, final_price: money(part.vat_mode === 'incl' ? amount * (1 + Number(part.vat_pct || 0) / 100) : amount) };
}
export function quoteDecisionPermission(action) {
  return action === 'quote_send_below_recommended_gp' ? 'approvals.decide_below_recommended_gp_quotes' : 'approvals.decide_customer_quotes';
}
export const isQuoteAction = action => ['quote_send', 'quote_send_below_recommended_gp'].includes(action);
export const directPricingAllowed = (can, review) => can('sales_pipeline.send_quote_without_approval') && (!review.below_recommended_gp || can('sales_pipeline.send_below_recommended_gp_without_approval'));
// A server conflict replaces calculated fields; recompute commercial totals because a
// review may carry the old submitted final_price alongside its corrected sp.
export function applyPricingReview(parts, review) {
  const byId = new Map((review?.parts || []).map(part => [Number(part.query_part_id), part]));
  return parts.map(part => {
    const fresh = byId.get(Number(part.query_part_id));
    return commercialLine(fresh ? { ...part, ...fresh, cp: money(fresh.supplier_quoted_price ?? fresh.cp), base_sp: money(fresh.base_sp), sp: money(fresh.sp) } : part);
  });
}
